// ============================================================================
// AccountDisabled — full-shell screen for the edge case where a user's
// status flipped to 'disabled' mid-session. The JWT bridge 403s disabled
// accounts at sign-in, so disabled users normally can't reach the app at
// all — but if an admin disables an account while the user is signed in,
// the next JWT refresh (~55min cycle) will carry status='disabled' and
// NavShell renders this instead of the authenticated shell.
// ============================================================================

import { ShieldOff, LogOut } from 'lucide-react';
import { Button } from './components';

interface Props {
  email: string | undefined;
  onSignOut: () => void;
}

export function AccountDisabled({ email, onSignOut }: Props) {
  return (
    <div className="flex h-full items-center justify-center bg-tcs-bg-muted p-6">
      <div className="w-full max-w-md rounded-xl border border-st-danger-border-strong bg-st-danger-bg/60 p-6 text-sm text-st-danger-fg-strong">
        <div className="flex items-start gap-3">
          <ShieldOff className="mt-0.5 h-5 w-5 shrink-0 text-st-danger-fg" />
          <div className="space-y-2">
            <p className="font-semibold">Your account has been disabled.</p>
            <p>
              {email ? (
                <>
                  Access for{' '}
                  <span className="font-mono text-xs">{email}</span> has been
                  revoked.
                </>
              ) : (
                'Access for this account has been revoked.'
              )}{' '}
              Contact your manager or{' '}
              <a
                href="mailto:ops@terminalcleaning.com"
                className="underline underline-offset-2"
              >
                ops@terminalcleaning.com
              </a>{' '}
              if you think this is a mistake.
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={onSignOut}
              icon={<LogOut className="h-4 w-4" />}
              className="mt-2"
            >
              Sign out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
